import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import {
  BehaviorSubject,
  catchError,
  filter,
  finalize,
  Observable,
  switchMap,
  take,
  throwError,
} from 'rxjs';
import { LoaderService } from '../services/loader.service';
import { TranslateStore } from '@ngx-translate/core';
import { AuthService } from '../services/auth.service';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { environment } from 'src/environments/environment';

@Injectable()
export class HttpInter implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);

  constructor(
    private $loader: LoaderService,
    private $ts: TranslateStore,
    private $auth: AuthService,
    private router: Router,
    private $toast: MatSnackBar,
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const isSSO = req.params.get('isSSO');
    let newParams = req.params.delete('isSSO');
    const hideToast = req.params.get('hideToast');
    newParams = newParams.delete('hideToast');
    if (!req.params.has('loader')) {
      this.$loader.show();
    }

    const host = isSSO ? environment.ssoHost : environment.serverHost;

    const initUrl = req.url;

    req = req.clone({
      setHeaders: {
        'Accept-Language': this.$ts.defaultLang || 'ru',
      },
      url:
        req.url.startsWith('/assets') || req.url.startsWith('http')
          ? req.url
          : `${host}${req.url}`,
    });

    if (this.$auth.isAuth() && initUrl !== 'auth/refresh-access-token') {
      if (!req.headers.has('Authorization'))
        req = req.clone({
          setHeaders: {
            Authorization: `Bearer ${this.$auth.getToken()}`,
          },
        });
    }

    const newReq = req.clone({
      params: newParams,
    });

    return next.handle(newReq).pipe(
      catchError((error: HttpErrorResponse) => {
        if (
          (initUrl === 'auth/refresh-access-token' && error.status === 401) ||
          error.error?.code === 'USER_NOT_FOUND'
        ) {
          this.router.navigate(['/login']).then(() => this.$auth.logOut());
          return throwError(() => error);
        }

        if (
          error.status === 401 &&
          !initUrl.includes('verify') &&
          !initUrl.includes('change')
        ) {
          // Если токен уже обновляется, добавляем запрос в очередь
          if (this.isRefreshing) {
            return this.refreshTokenSubject.pipe(
              filter((token) => token !== null),
              take(1),
              switchMap((token) => {
                const authReq = newReq.clone({
                  setHeaders: {
                    Authorization: `Bearer ${token}`,
                  },
                });
                return next.handle(authReq);
              }),
            );
          } else {
            this.isRefreshing = true;
            this.refreshTokenSubject.next(null);

            return this.$auth.updateToken().pipe(
              switchMap((res) => {
                this.$auth.setToken(res.access_token, res.refresh_token);
                this.refreshTokenSubject.next(res.access_token); // Разблокируем очередь

                const authReq = newReq.clone({
                  setHeaders: {
                    Authorization: `Bearer ${res.access_token}`,
                  },
                });
                return next.handle(authReq);
              }),
              catchError((err) => {
                this.refreshTokenSubject.next(null); // Сбрасываем очередь при ошибке
                this.router
                  .navigate(['/login'])
                  .then(() => this.$auth.logOut());
                return throwError(() => err);
              }),
              finalize(() => {
                this.isRefreshing = false; // Разблокируем новые запросы
              }),
            );
          }
        }

        if ([403, 404, 405].includes(error.status) && this.$auth.isAuth()) {
          this.router.navigate([
            '/error',
            { queryParams: error.error || error },
          ]);
          return throwError(() => error);
        }

        if (error.error.is_server_error && !hideToast) {
          this.$toast.open(error.error?.message || 'server Error', undefined, {
            duration: 2500,
            panelClass: ['error'],
          });
          return throwError(() => error);
        }

        return throwError(() => error);
      }),
      finalize(() => this.$loader.hide()),
    );
  }
}
